import Button from 'Components/Base/Button'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import { FormikErrors, getIn, useFormik } from 'formik'
import { useAppSelector } from 'hooks/hooks'
import useNewEnrollment from 'hooks/useNewEnrollment'
import usePatientIntake from 'hooks/usePatientIntake'
import React, { useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import ReactSlider from 'react-slider'
import { getAccessToken } from 'reducers/authSlice'
import styled, { useTheme } from 'styled-components'
import * as Yup from 'yup'

interface props {
  handleSubmit: Function
}

export default function CTLLifestyle({ handleSubmit }: props) {
  const theme = useTheme()
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [meds, setMeds] = useState<number>(18)
  const [sliderValue, setSliderValue] = useState(0)
  const accessToken = useAppSelector(getAccessToken)
  const { getLifeStyleInfo, postEnrollmentStatus } = usePatientIntake({ token: accessToken })
  const { postCTLLifestyle } = useNewEnrollment({ token: accessToken })

  useEffect(() => {
    getLifeStyleInfo().then((res) => {
      formik.setFieldValue('heightFeet', res.data.heightFt)
      formik.setFieldValue('heightInches', res.data.heightIn)
      formik.setFieldValue('weight', res.data.weight)
      formik.setFieldValue('weightBeforeMeds', res.data.weightBeforeMeds ?? 0)
      if (res.data.weightBeforeMeds) {
        setMeds(17)
      }
      formik.setFieldValue('goalWeight', res.data.goalWeight)
      if (res.data.responses.length > 0) {
        if (res.data.responses.find((x) => x.questionId === 36)) {
          const sliderQuestion = res.data.responses.find((x) => x.questionId === 36)
          switch (sliderQuestion.answerId) {
            case 35:
              changeSlider(1)
              break
            case 36:
              changeSlider(2)
              break
            case 37:
              changeSlider(3)
              break
            case 38:
              changeSlider(4)
              break
            case 39:
              changeSlider(5)
              break
            case 40:
              changeSlider(6)
              break
            case 41:
              changeSlider(7)
              break
            case 42:
              changeSlider(8)
              break
            case 43:
              changeSlider(9)
              break
            case 44:
              changeSlider(10)
              break

            default:
              break
          }
        }

        formik.setFieldValue('activityLevel', res.data.responses.find((x) => x.questionId === 33)?.answerId)
        formik.setFieldValue('reasonForChange', res.data.responses.find((x) => x.questionId === 35)?.answerId)
        formik.setFieldValue('dietVariety', res.data.responses.find((x) => x.questionId === 37)?.answerId)
        formik.setFieldValue('cookingSkills', res.data.responses.find((x) => x.questionId === 39)?.answerId)
        formik.setFieldValue('dietStrategy', res.data.responses.find((x) => x.questionId === 40)?.note)
        formik.setFieldValue('livingSituation', res.data.responses.find((x) => x.questionId === 41)?.answerId)
        formik.setFieldValue('householdMembersTryingToLoseWeight', res.data.responses.find((x) => x.questionId === 42)?.answerId)
        formik.setFieldValue('timeForYourself', res.data.responses.find((x) => x.questionId === 43)?.answerId)
        formik.setFieldValue('obligations', res.data.responses.find((x) => x.questionId === 44)?.answerId)
      }
    })
  }, [])

  const setMobileSetting = () => {
    if (window.innerWidth < 800) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  useEffect(() => {
    setMobileSetting()
    window.addEventListener('resize', () => {
      setMobileSetting()
    })
  }, [])

  const validationSchema = Yup.object().shape({
    heightFeet: Yup.number().required('Height (ft) is required').min(2, 'Height must be greater than 3 feet'),
    heightInches: Yup.number().required('Height (in) is required'),
    weight: Yup.number().required('Weight is required').min(79, 'Weight must be greater than 80lbs'),
    goalWeight: Yup.number().required('Weight is required').min(79, 'Weight must be greater than 80lbs'),
    weightBeforeMeds: meds === 17 ? Yup.number().required('Weight is required').min(79, 'Weight must be greater than 80lbs') : Yup.number(),
    reasonForChange: Yup.number().min(1).required('Activity is required'),
    readinessToChange: Yup.number().min(0).required('Activity is required'),
    dietVariety: Yup.number().min(1).required('Activity is required'),
    mealsSameTime: Yup.number().min(1).required('Activity is required'),
    cookingSkills: Yup.number().min(1).required('Activity is required'),
    dietStrategy: Yup.string(),
    livingSituation: Yup.number().min(1).required('Activity is required'),
    householdMembersTryingToLoseWeight: Yup.number().min(1).required('Activity is required'),
    timeForYourself: Yup.number().min(1).required('Activity is required'),
    obligations: Yup.number().min(1).required('Activity is required'),
    activityLevel: Yup.number().min(1).required('Activity is required'),
    carrierPId: Yup.number(),
  })

  const initialValues: any = {
    heightFeet: '',
    heightInches: '',
    weight: '',
    goalWeight: '',
    weightBeforeMeds: '',
    reasonForChange: undefined,
    readinessToChange: 35,
    dietVariety: undefined,
    mealsSameTime: 17,
    cookingSkills: undefined,
    dietStrategy: undefined,
    livingSituation: undefined,
    householdMembersTryingToLoseWeight: undefined,
    timeForYourself: undefined,
    obligations: undefined,
    obligationsNote: undefined,
    activityLevel: undefined,
    reasonForChangeNote: undefined,
    livingSituationNote: undefined,
  }

  const onSubmit = () => {
    postCTLLifestyle(formik.values)
    const request = { statusId: 12 }
    postEnrollmentStatus(request)
    handleSubmit()
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  const changeSlider = (value: number) => {
    let answerId = 35
    setSliderValue(value)

    switch (value) {
      case 1:
        answerId = 35
        break
      case 2:
        answerId = 36
        break
      case 3:
        answerId = 37
        break
      case 4:
        answerId = 38
        break
      case 5:
        answerId = 39
        break
      case 6:
        answerId = 40
        break
      case 7:
        answerId = 41
        break
      case 8:
        answerId = 42
        break
      case 9:
        answerId = 43
        break
      case 10:
        answerId = 44
        break

      default:
        break
    }
    formik.setFieldValue('readinessToChange', answerId)
  }

  function getStyles(fieldName: string, errors: FormikErrors<any>) {
    if (getIn(errors, fieldName)) {
      return {
        border: '2px solid red',
        boxShadow: 'none',
      }
    }
  }

  return (
    <Container>
      <Card>
        <Header>Your Lifestyle & Habits</Header>
        <Para>Just a few questions about your everyday habits</Para>

        <Form>
          <StyledFormSet>
            <Row styles={isMobile ? {} : { marginRight: 'auto' }}>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('heightFeet', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,

                  ...getStyles('heightFeet', formik.errors),
                }}
                value={formik.values.heightFeet}
                placeholder="Height (ft)"
              />
            </Row>
            <Row>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('heightInches', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('heightInches', formik.errors),
                }}
                value={formik.values.heightInches}
                placeholder="Height (in)"
              />
            </Row>
          </StyledFormSet>
          <StyledFormSet>
            <Row styles={isMobile ? {} : { marginRight: 'auto' }}>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('weight', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('weight', formik.errors),
                }}
                value={formik.values.weight}
                placeholder="Weight (lbs)"
              />
            </Row>
            <Row>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('goalWeight', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('goalWeight', formik.errors),
                }}
                value={formik.values.goalWeight}
                placeholder="Goal Weight (lbs)"
              />
            </Row>
          </StyledFormSet>

          <DropdownWrapper>
            <Select>
              <Option value="" hidden>
                Sex Assigned at Birth
              </Option>
              <Option value="Option1">Male</Option>
              <Option value="Option2">Female</Option>
            </Select>
            <Arrow>
              <Icon name="chevron-down" />
            </Arrow>
          </DropdownWrapper>
        </Form>

        <Para>
          <b>Are you currently taking a medicine to help you lose weight?</b>
        </Para>

        <Switch>
          <Checkbox type="checkbox" checked={meds === 17} onChange={() => setMeds(meds === 18 ? 17 : 18)} />
          <Slider />
        </Switch>
        {meds === 17 && (
          <>
            <Para>
              <b>What was your weight before you started taking this medicine?</b>
            </Para>

            <Row styles={isMobile ? {} : { marginRight: 'auto' }}>
              <StyledFormInput
                type="number"
                onChange={(value) => formik.setFieldValue('weightBeforeMeds', value.currentTarget.value)}
                style={{
                  border: '2px solid transparent',
                  borderRadius: 4,
                  ...getStyles('weightBeforeMeds', formik.errors),
                }}
                value={formik.values.weightBeforeMeds}
                placeholder="Weight (lbs)"
              />
            </Row>
          </>
        )}
        <Para>
          <b>How would you describe your activity level?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('activityLevel', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('activityLevel', value.currentTarget.value)} value={formik.values.activityLevel}>
            <Option value="" hidden>
              Select an Option
            </Option>
            <Option value={55} style={{ whiteSpace: 'normal' }}>
              Sedentary (little or no exercise, desk job)
            </Option>
            <Option value={56} style={{ whiteSpace: 'normal' }}>
              Lightly active (light exercise / sports 1-3 days/week)
            </Option>
            <Option value={57} style={{ whiteSpace: 'normal' }}>
              Moderately active (moderate exercise / sports 6-7 days/week)
            </Option>
            <Option value={58} style={{ whiteSpace: 'normal' }}>
              Very active (hard exercise every day, or moderate exercise 2 times/day)
            </Option>
            <Option value={90} style={{ whiteSpace: 'normal' }}>
              Extra active (hard exercise 2 or more times per day, or training for marathon, or triathlon, etc.)
            </Option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>What’s motivating you to make a change to your weight?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('reasonForChange', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('reasonForChange', value.currentTarget.value)} value={formik.values.reasonForChange}>
            <Option value="" hidden>
              Select an Option
            </Option>
            <option value={64}>I’m feeling ready today</option>
            <option value={65}>I’m unhappy with my weight</option>
            <option value={66}>I have some fresh motivation</option>
            <option value={67}>Something else</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>Please indicate your readiness to change. (1 = Not ready at all / 10 = Extremely ready)</b>
        </Para>
        <div style={{ margin: '20px 0px 40px' }}>
          <ReactSlider
            className="horizontal-slider"
            thumbClassName="slider-thumb"
            trackClassName="slider-track"
            value={sliderValue}
            min={0}
            max={10}
            onChange={(value: number) => changeSlider(value)}
            renderThumb={(props: any, state: any) => <div {...props}>{state.valueNow}</div>}
          />
        </div>
        <Para>
          <b>How would you describe the variety in your weekly diet?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('dietVariety', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('dietVariety', value.currentTarget.value)} value={formik.values.dietVariety}>
            <option value={0} hidden>
              Describe your diet
            </option>
            <option value={68}>I mostly eat the same foods every day</option>
            <option value={69}>I rotate between a few different foods for each meal</option>
            <option value={70}>I eat some of the same foods, but there is plenty of variety</option>
            <option value={71}>My diet varies a lot from day to day</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>How would you describe your cooking skills?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('cookingSkills', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('cookingSkills', value.currentTarget.value)} value={formik.values.cookingSkills}>
            <option value={0} hidden>
              Describe your cooking skills
            </option>
            <option value={72}>I am a pretty good cook</option>
            <option value={73}>I do well enough</option>
            <option value={74}>I am learning</option>
            <option value={75}>I am not much of a cook</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>Is there a diet strategy you are currently using?</b>
        </Para>

        <StyledFormInputDiet
          type="text"
          onChange={(value) => formik.setFieldValue('dietStrategy', value.currentTarget.value)}
          style={{
            border: '2px solid transparent',
            borderRadius: 4,
            marginLeft: 'auto',
            marginRight: 'auto',
            ...getStyles('dietStrategy', formik.errors),
          }}
          value={formik.values.dietStrategy}
          placeholder="Low Carb, Vegetarian, Mediterranean, etc"
        />

        <Para>
          <b>What best describes your living situation?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('livingSituation', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('livingSituation', value.currentTarget.value)} value={formik.values.livingSituation}>
            <option value={0} hidden>
              Describe your Living situation
            </option>
            <option value={76}>I live on my own</option>
            <option value={77}>I live with my family</option>
            <option value={78}>I live with a partner</option>
            <option value={79}>I live with roommates</option>
            <option value={80}>I live in shared housing</option>
            <option value={67}>Something else</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>Are others in your household trying to lose weight?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('householdMembersTryingToLoseWeight', formik.errors),
          }}
        >
          <Select
            onChange={(value) => formik.setFieldValue('householdMembersTryingToLoseWeight', value.currentTarget.value)}
            value={formik.values.householdMembersTryingToLoseWeight}
          >
            <Option value="" hidden>
              Select an Option
            </Option>
            <option value={18}>No</option>
            <option value={81}>I'm not sure</option>
            <option value={17}>Yes</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>How much time do you have for yourself in a typical week?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('timeForYourself', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('timeForYourself', value.currentTarget.value)} value={formik.values.timeForYourself}>
            <option value={0} hidden>
              How much time do you have for yourself in a typical week?
            </option>
            <option value={82}>Nearly none</option>
            <option value={83}>A little bit here and there</option>
            <option value={84}>A good amount</option>
            <option value={85}>As much as I want</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>

        <Para>
          <b>What are your day-to-day obligations?</b>
        </Para>

        <DropdownWrapper
          style={{
            ...getStyles('obligations', formik.errors),
          }}
        >
          <Select onChange={(value) => formik.setFieldValue('obligations', value.currentTarget.value)} value={formik.values.obligations}>
            <option value={0} hidden>
              What are your day-to-day obligations?
            </option>
            <option value={86}>I am a caregiver</option>
            <option value={87}>I have a full-time job</option>
            <option value={88}>I have a part-time job</option>
            <option value={89}>I am in school</option>
            <option value={67}>Something else</option>
          </Select>
          <Arrow>
            <Icon name="chevron-down" />
          </Arrow>
        </DropdownWrapper>
        {Object.keys(formik.errors).length !== 0 && <ErrorText>Please fill out all required fields.</ErrorText>}

        <Row styles={{ width: '100%', justifyContent: 'space-between', marginTop: 30 }}>
          <Button text="Submit" styles={{ marginBottom: 30, backgroundColor: theme.colors.status.good }} width={150} onClick={formik.handleSubmit} />
        </Row>
      </Card>
      <Image src="https://triacentral.blob.core.windows.net/dev-storage/portal/enrollment/CTL%20Review%201.png" />
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  display: flex;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  height: fit-content;
  justify-content: space-between;
  @media only screen and (max-width: 600px) {
    margin-top: 20px;
  }
`

const ErrorText = styled.p`
  font-size: 18px;
  margin-top: 10px;
  color: ${({ theme }) => `${theme.colors.status.bad}`};
`

const Card = styled.div`
  font-family: Raleway;
  max-width: 600px;
  height: fit-content;
  min-height: 640px;
  padding: 40px;
  text-align: left;
  @media only screen and (max-width: 600px) {
    margin-top: 20px;
    padding: 0px 20px 40px;
  }
`

const Header = styled.h1`
  font-weight: 700;
  font-size: 36px;
  margin-bottom: 20px;
`

const Para = styled.p`
  font-size: 18px;
  margin-bottom: 10px;
  margin-top: 20px;
`

const StyledFormSet = styled.div`
  flex-direction: row;
  display: flex;
  min-width: 100%;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`
const StyledFormInput = styled(Form.Control)`
  background-color: #edfdff;
  text-align: center;
  border-radius: 3px;
  box-shadow: inset 0px 1px 3px;
  min-height: 50px;
  margin-bottom: 10px;
  width: 200px;
  @media (max-width: 600px) {
    width: 100%;
  }
`
const StyledFormInputDiet = styled(Form.Control)`
  background-color: #edfdff;
  text-align: center;
  border-radius: 3px;
  box-shadow: inset 0px 1px 3px;
  min-height: 50px;
  margin-bottom: 10px;
  width: 100%;
  @media (max-width: 600px) {
    width: 100%;
  }
`

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%; /* adjust as needed */
`

const Select = styled.select`
  width: 100%;
  padding: 10px 40px 10px 12px; /* space for arrow */
  font-size: 16px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  appearance: none; /* remove default arrow */
  background-color: #fff;
  color: #333;
  outline: none;
  cursor: pointer;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const Option = styled.option`
  max-width: 250px;
  white-space: normal; /* Enable wrapping */
  word-wrap: break-word;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const Arrow = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 14px;
  color: #666;
`

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 24px; /* thinner height */
  margin-bottom: 20px;
`

const Checkbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${({ theme }) => `${theme.colors.status.good}`};
  }

  &:checked + span:before {
    transform: translateX(36px); /* adjust based on knob size */
  }
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px; /* smaller knob */
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`

const Image = styled.img`
  max-height: 530px;
  padding: 40px;
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`
